/**
 * WritingCanvas — the literary heart of the editor. An uncontrolled
 * contentEditable region (keyed per-chapter so switching chapters resets
 * cleanly) with a comfortable reading measure and serif type.
 */

import React, { useEffect, useRef } from 'react';
import { cx } from '@/components/site/primitives';

const WritingCanvas = React.forwardRef(function WritingCanvas(
  { chapter, onInput, focusMode = false },
  forwardedRef
) {
  const innerRef = useRef(null);

  useEffect(() => {
    if (innerRef.current) {
      innerRef.current.innerHTML = chapter?.content || '';
    }
    // Reset only when the chapter identity changes, not on every content edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapter?.id]);

  useEffect(() => {
    if (typeof forwardedRef === 'function') forwardedRef(innerRef.current);
    else if (forwardedRef) forwardedRef.current = innerRef.current;
  });

  if (!chapter) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 py-24 text-center text-sm text-subtle">
        Select or add a chapter to start writing.
      </div>
    );
  }

  return (
    <div className={cx('flex flex-1 justify-center px-6 py-10 sm:px-10', focusMode && 'py-16')}>
      <div className="w-full max-w-[700px]">
        <input
          value={chapter.title}
          onChange={(e) => onInput({ type: 'title', value: e.target.value })}
          placeholder="Chapter title"
          data-testid="my-writing-chapter-title"
          className="w-full bg-transparent font-display text-3xl font-semibold text-ink outline-none placeholder:text-subtle/50"
        />
        <div
          ref={innerRef}
          contentEditable
          suppressContentEditableWarning
          data-testid="my-writing-canvas"
          onInput={(e) => onInput({ type: 'content', value: e.currentTarget.innerHTML })}
          className="prose-writing mt-6 min-h-[50vh] font-display text-[19px] leading-[1.85] text-ink outline-none empty:before:text-subtle/50 empty:before:content-[attr(data-placeholder)] [&_blockquote]:border-l-2 [&_blockquote]:border-coral/50 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-subtle [&_h2]:mt-6 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-semibold [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-4 [&_ul]:list-disc [&_a]:text-coral [&_a]:underline"
          data-placeholder="Once upon a page…"
        />
      </div>
    </div>
  );
});

export default WritingCanvas;
